/**
 * Teaching Team Hooks
 * Custom React hooks for teaching team data management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../utils/teachingTeamApi';

/**
 * Hook to fetch and manage teaching teams list
 */
export const useTeachingTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeachingTeams();
      setTeams(data.teaching_teams || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const createTeam = useCallback(async (teamData) => {
    const result = await api.createTeachingTeam(teamData);
    setTeams(prev => [...prev, result]);
    return result;
  }, []);

  return { teams, loading, error, refetch: fetchTeams, createTeam };
};

/**
 * Hook to fetch and manage a single teaching team
 */
export const useTeachingTeam = (teamId) => {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTeam = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeachingTeam(teamId);
      setTeam(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const updateTeam = useCallback(async (teamData) => {
    const result = await api.updateTeachingTeam(teamId, teamData);
    setTeam(result);
    return result;
  }, [teamId]);

  const updateBookingMode = useCallback(async (bookingMode) => {
    const result = await api.updateBookingMode(teamId, bookingMode);
    setTeam(prev => ({ ...prev, booking_mode: bookingMode }));
    return result;
  }, [teamId]);

  return { team, loading, error, refetch: fetchTeam, updateTeam, updateBookingMode };
};

/**
 * Hook to fetch team dashboard data
 */
export const useTeamDashboard = (teamId) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamDashboard(teamId);
      setDashboard(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { dashboard, loading, error, refetch: fetchDashboard };
};

/**
 * Hook to manage team memberships
 */
export const useTeamMemberships = (teamId) => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMemberships = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamMemberships(teamId);
      setMemberships(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const inviteMember = useCallback(async (memberData) => {
    const result = await api.inviteMember(teamId, memberData);
    await fetchMemberships();
    return result;
  }, [teamId, fetchMemberships]);

  const updateMembership = useCallback(async (membershipId, memberData) => {
    const result = await api.updateMembership(teamId, membershipId, memberData);
    setMemberships(prev => prev.map(m => m.id === membershipId ? result : m));
    return result;
  }, [teamId]);

  const removeMembership = useCallback(async (membershipId) => {
    const result = await api.removeMembership(teamId, membershipId);
    setMemberships(prev => prev.filter(m => m.id !== membershipId));
    return result;
  }, [teamId]);

  return {
    memberships,
    loading,
    error,
    refetch: fetchMemberships,
    inviteMember,
    updateMembership,
    removeMembership
  };
};

/**
 * Hook to manage pending invitations for current user
 */
export const usePendingInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPendingInvitations();
      setInvitations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const acceptInvitation = useCallback(async (invitationId) => {
    const result = await api.acceptInvitation(invitationId);
    setInvitations(prev => prev.filter(i => i.id !== invitationId));
    return result;
  }, []);

  const rejectInvitation = useCallback(async (invitationId) => {
    await api.rejectInvitation(invitationId);
    setInvitations(prev => prev.filter(i => i.id !== invitationId));
  }, []);

  return {
    invitations,
    loading,
    error,
    refetch: fetchInvitations,
    acceptInvitation,
    rejectInvitation
  };
};

/**
 * Hook to manage team applications
 */
export const useTeamApplications = (teamId) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApplications = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamApplications(teamId);
      setApplications(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const acceptApplication = useCallback(async (applicationId, revenueShareType, revenueShareValue, revenueShareValuePlatform) => {
    const result = await api.acceptApplication(applicationId, revenueShareType, revenueShareValue, revenueShareValuePlatform);
    setApplications(prev => prev.filter(a => a.id !== applicationId));
    return result;
  }, []);

  const rejectApplication = useCallback(async (applicationId, reason) => {
    await api.rejectApplication(applicationId, reason);
    setApplications(prev => prev.filter(a => a.id !== applicationId));
  }, []);

  return {
    applications,
    loading,
    error,
    refetch: fetchApplications,
    acceptApplication,
    rejectApplication
  };
};

/**
 * Hook to manage team courses
 */
export const useTeamCourses = (teamId) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourses = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamCourses(teamId);
      setCourses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const createCourse = useCallback(async (courseData) => {
    const result = await api.createTeamCourse(teamId, courseData);
    setCourses(prev => [...prev, result]);
    return result;
  }, [teamId]);

  const updateCourse = useCallback(async (courseId, courseData) => {
    const result = await api.updateTeamCourse(teamId, courseId, courseData);
    setCourses(prev => prev.map(c => c.id === courseId ? result : c));
    return result;
  }, [teamId]);

  const deleteCourse = useCallback(async (courseId) => {
    await api.deleteTeamCourse(teamId, courseId);
    setCourses(prev => prev.filter(c => c.id !== courseId));
  }, [teamId]);

  return {
    courses,
    loading,
    error,
    refetch: fetchCourses,
    createCourse,
    updateCourse,
    deleteCourse
  };
};

/**
 * Hook to manage team bookings
 */
export const useTeamBookings = (teamId, initialFilters = {}) => {
  const [bookings, setBookings] = useState([]);
  const [groupClassSessions, setGroupClassSessions] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, total_pages: 0, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef(initialFilters);

  const fetchBookings = useCallback(async (newFilters = {}) => {
    if (!teamId) return;
    const currentFilters = { ...filtersRef.current, ...newFilters };
    filtersRef.current = currentFilters;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamBookings(teamId, currentFilters);
      setBookings(data.bookings);
      setGroupClassSessions(data.group_class_sessions || []);
      setMeta(data.meta);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchBookings();
  }, [teamId, fetchBookings]);

  const completeBooking = useCallback(async (bookingId, reason) => {
    const result = await api.completeBooking(bookingId, reason);
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...result } : b));
    return result;
  }, []);

  const cancelBooking = useCallback(async (bookingId, reason) => {
    const result = await api.cancelBooking(bookingId, reason);
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...result } : b));
    return result;
  }, []);

  const updateBooking = useCallback(async (bookingId, data) => {
    const result = await api.updateBooking(bookingId, data);
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...result } : b));
    return result;
  }, []);

  return {
    bookings,
    groupClassSessions,
    meta,
    loading,
    error,
    filters: filtersRef.current,
    refetch: fetchBookings,
    completeBooking,
    cancelBooking,
    updateBooking
  };
};

/**
 * Hook to manage pending students
 */
export const usePendingStudents = (teamId) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStudents = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPendingStudents(teamId);
      setStudents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { students, loading, error, refetch: fetchStudents };
};

/**
 * Hook to manage team students
 */
export const useTeamStudents = (teamId, initialFilters = {}) => {
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, total_pages: 0, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef(initialFilters);

  const fetchStudents = useCallback(async (newFilters = {}) => {
    if (!teamId) return;
    const currentFilters = { ...filtersRef.current, ...newFilters };
    filtersRef.current = currentFilters;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamStudents(teamId, currentFilters);
      setStudents(data.students || data);
      if (data.meta) setMeta(data.meta);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    filtersRef.current = initialFilters;
    fetchStudents();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { students, meta, loading, error, filters: filtersRef.current, refetch: fetchStudents };
};

/**
 * Hook to manage my payslips
 */
export const useMyPayslips = (initialFilters = {}) => {
  const [payslips, setPayslips] = useState([]);
  const [teams, setTeams] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, total_pages: 0, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef(initialFilters);

  const fetchPayslips = useCallback(async (newFilters = {}) => {
    const currentFilters = { ...filtersRef.current, ...newFilters };
    filtersRef.current = currentFilters;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMyPayslips(currentFilters);
      setPayslips(data.payslips || data);
      if (data.teams) setTeams(data.teams);
      if (data.meta) setMeta(data.meta);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  return { payslips, teams, meta, loading, error, filters: filtersRef.current, refetch: fetchPayslips };
};

/**
 * Hook to manage team payslips (for owner/admin)
 */
export const useTeamPayslips = (teamId, initialFilters = {}) => {
  const [payslips, setPayslips] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, total_pages: 0, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef(initialFilters);

  const fetchPayslips = useCallback(async (newFilters = {}) => {
    if (!teamId) return;
    const currentFilters = { ...filtersRef.current, ...newFilters };
    filtersRef.current = currentFilters;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTeamPayslips(teamId, currentFilters);
      setPayslips(data.payslips || data);
      if (data.meta) setMeta(data.meta);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchPayslips();
  }, [teamId, fetchPayslips]);

  const markPaid = useCallback(async (payslipId) => {
    const result = await api.markPayslipPaid(teamId, payslipId);
    setPayslips(prev => prev.map(p => p.id === payslipId ? result : p));
    return result;
  }, [teamId]);

  const generatePayslips = useCallback(async (billingMonth) => {
    const result = await api.generatePayslips(teamId, billingMonth);
    await fetchPayslips();
    return result;
  }, [teamId, fetchPayslips]);

  return {
    payslips,
    meta,
    loading,
    error,
    filters: filtersRef.current,
    refetch: fetchPayslips,
    markPaid,
    generatePayslips
  };
};

/**
 * Hook to fetch payslip preview (projected earnings)
 */
export const usePayslipPreview = (teamId, billingMonth = null) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(!!teamId);
  const [error, setError] = useState(null);

  const fetchPreview = useCallback(async (billingMonth) => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPayslipPreview(teamId, billingMonth);
      setPreview(data.current_user_preview);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchPreview(billingMonth);
  }, [fetchPreview, billingMonth]);

  return { preview, loading, error, refetch: fetchPreview };
};

/**
 * Hook to fetch settled lesson details for a payslip (from settled bookings)
 */
export const useSettledLessonDetails = (teamId, billingMonth) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!teamId || !billingMonth) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getSettledLessonDetails(teamId, billingMonth);
      setDetails(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, billingMonth]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return { details, loading, error };
};

/**
 * Hook to manage my course balances
 */
export const useMyCourseBalances = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBalances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMyCourseBalances();
      setBalances(data.balances || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  return { balances, loading, error, refetch: fetchBalances };
};
